import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import { busyDimClass } from '../../elements/ui/motion';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { benchmarkDay, benchmarkTimestamp, FarmBenchmark, FarmBenchmarkMetric } from '../../models/farm-benchmark';
import { farmBenchmarkColors as c } from './colors';
import { formatDelta, formatValue, metricLabel, standingOf } from './format';
import BenchmarkBar from './BenchmarkBar';
import BenchmarkTrend from './BenchmarkTrend';

/** Name, figures, bar, trend. One template for the header and every row so the columns line up. */
const ROW_GRID = 'grid grid-cols-1 items-center gap-x-6 gap-y-2 md:grid-cols-[11rem_8rem_minmax(0,1fr)_10rem]';

function Legend() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-4 pt-2 text-xs text-gray-600">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.better }} />
        {t('f-benchmark-legend-better')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: c.worse }} />
        {t('f-benchmark-legend-worse')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-0.5 rounded-full" style={{ backgroundColor: c.average }} />
        {t('f-benchmark-legend-average')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-4 rounded-[2px]" style={{ backgroundColor: c.range }} />
        {t('f-benchmark-legend-range')}
      </span>
    </div>
  );
}

function MetricRow({ metric }: { metric: FarmBenchmarkMetric }) {
  const { t } = useTranslation();
  const standing = standingOf(metric, metric.lowerIsBetter);
  const { farmValue, othersAverage, unit } = metric;

  return (
    <div className={`${ROW_GRID} py-3`}>
      <span className="text-sm font-medium text-gray-900">{metricLabel(metric.metric, t)}</span>

      <div className="min-w-0">
        {farmValue === null ? (
          <p className="text-sm text-gray-400">{t('f-benchmark-no-data')}</p>
        ) : (
          <p className="text-sm font-semibold tabular-nums text-gray-900">{formatValue(farmValue, unit)}</p>
        )}
        <p className="text-xs tabular-nums text-gray-500">
          {farmValue !== null && othersAverage !== null
            ? t('f-benchmark-vs-average', { delta: formatDelta(farmValue - othersAverage, unit) })
            : othersAverage !== null
              ? t('f-benchmark-average-value', { value: formatValue(othersAverage, unit) })
              : t('f-benchmark-no-other-farms')}
        </p>
      </div>

      <BenchmarkBar metric={metric} standing={standing} />
      <BenchmarkTrend metric={metric} standing={standing} />
    </div>
  );
}

const RowsSkeleton = () => (
  <div className="divide-y divide-gray-100">
    {[0, 1, 2, 3, 4, 5].map((index) => (
      <div key={index} className={`${ROW_GRID} py-3`}>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-9 w-40" />
      </div>
    ))}
  </div>
);

export interface FarmBenchmarkCardProps {
  benchmark: FarmBenchmark | null;
  isBusy: boolean;
  /** True once a fetch has settled. Together with a null `benchmark`, it means the fetch failed. */
  loaded: boolean;
}

/**
 * This farm against the other farms over the last twelve months, one row per metric in lifecycle order.
 *
 * The other farms appear only as an average, a lowest-to-highest band and a count - never by name and never
 * as a position among them. Whether being above the average is good comes from the metric's
 * `lowerIsBetter`, not from anything known here.
 */
export default function FarmBenchmarkCard({ benchmark, isBusy, loaded }: FarmBenchmarkCardProps) {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const metrics = benchmark?.metrics ?? [];
  const { periodFrom, periodTo, calculatedAt } = benchmark ?? {};

  const renderBody = () => {
    if (!benchmark) {
      if (isBusy || !loaded) return <RowsSkeleton />;
      // The hook has already toasted the error; this only keeps the card from looking empty-but-fine.
      return <p className="py-8 text-center text-sm text-gray-400">{t('f-benchmark-error')}</p>;
    }

    if (!calculatedAt || metrics.length === 0) {
      return <p className="py-8 text-center text-sm text-gray-400">{t('f-benchmark-not-calculated')}</p>;
    }

    return (
      <div className={busyDimClass(isBusy)}>
        <div className={`${ROW_GRID} hidden border-b border-gray-100 pb-2 md:grid`}>
          <span />
          <span className="text-[11px] uppercase tracking-wide text-gray-400">{t('f-benchmark-legend-farm')}</span>
          <span className="text-[11px] uppercase tracking-wide text-gray-400">{t('f-benchmark-column-period')}</span>
          <span className="text-[11px] uppercase tracking-wide text-gray-400">{t('f-benchmark-column-trend')}</span>
        </div>

        <div className="divide-y divide-gray-100">
          {metrics.map((metric) => (
            <MetricRow key={metric.metric} metric={metric} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="space-y-1 pb-4">
        <h3 className="text-base font-semibold text-gray-900">{t('f-benchmark-title')}</h3>
        <p className="text-xs text-gray-500">{t('f-benchmark-subtitle')}</p>
        {calculatedAt && (
          <p className="text-xs text-gray-500">
            {periodFrom && periodTo && (
              <React.Fragment>
                {t('f-benchmark-period', {
                  from: formatDate(benchmarkDay(periodFrom)),
                  to: formatDate(benchmarkDay(periodTo))
                })}
                {' · '}
              </React.Fragment>
            )}
            {t('f-benchmark-calculated-at', {
              date: formatDate(benchmarkTimestamp(calculatedAt)),
              time: formatTime(benchmarkTimestamp(calculatedAt))
            })}
          </p>
        )}
        {calculatedAt && metrics.length > 0 && <Legend />}
      </CardHeader>

      <CardContent>{renderBody()}</CardContent>
    </Card>
  );
}
